import { formatDate } from '@/lib/date';
import {
  type AuditValue,
  type EmployeeAuditEntry,
  type EmployeeAuditField,
  type EmployeeAuditSource,
} from '@/types/employee-audit';

/**
 * How the History tab words an entry [HW-795]. Kept out of the component
 * because the repo's vitest runs in a node environment — same reasoning as
 * profile-tabs.ts.
 */

export const HISTORY_FIELD_LABELS: Record<EmployeeAuditField, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  preferredName: 'Preferred name',
  workEmail: 'Work email',
  status: 'Status',
  startDate: 'Start date',
  probationEndDate: 'Probation end date',
  terminationDate: 'Termination date',
  nextReviewDate: 'Next review date',
  position: 'Position',
  department: 'Department',
  division: 'Division',
  seniorityLevel: 'Seniority level',
  employmentType: 'Employment type',
  managers: 'Managers',
  reportsTo: 'Reports to',
  dateOfBirth: 'Date of birth',
  gender: 'Gender',
  phone: 'Phone',
  personalEmail: 'Personal email',
  address: 'Address',
  country: 'Country',
  city: 'City',
  postalCode: 'Postal code',
  nationality: 'Nationality',
  corporateCvUrl: 'Corporate CV',
  linkedinUrl: 'LinkedIn',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  viber: 'Viber',
  signal: 'Signal',
  discord: 'Discord',
  paymentDetails: 'Payment details',
  education: 'Education',
};

const MESSENGER_FIELDS = new Set<EmployeeAuditField>([
  'telegram',
  'whatsapp',
  'viber',
  'signal',
  'discord',
]);

/**
 * The order the History tab shows fields in: one flat list sorted by label
 * alphabetically (compared in 'en', so the browser's locale cannot reorder
 * it), used by the filter dropdown and by the rows of one save, so the feed and
 * the filter agree. EMPLOYEE_AUDIT_FIELDS mirrors the
 * backend snapshot, whose order reads as random, so the display order is derived
 * here from the labels instead of being written out.
 */
export const HISTORY_FIELD_ORDER: EmployeeAuditField[] = (
  Object.keys(HISTORY_FIELD_LABELS) as EmployeeAuditField[]
).sort((a, b) => HISTORY_FIELD_LABELS[a].localeCompare(HISTORY_FIELD_LABELS[b], 'en'));

export interface HistoryFieldFilterOption {
  value: EmployeeAuditField;
  label: string;
}

/**
 * The field filter options for one reader: one flat list in HISTORY_FIELD_ORDER
 * (alphabetical by label; the "All fields" option goes above it). The server
 * hides messenger fields from readers without employees.emergency.view (unless it
 * is their own profile), nextReviewDate without employees.salary.view and
 * education without employees.profile.view (HW-1008), and a filter on a hidden
 * field returns an empty page, so those are not offered.
 */
export function historyFieldFilters(access: {
  canViewMessengers: boolean;
  canViewSalaryScalars: boolean;
  canViewEducation: boolean;
}): HistoryFieldFilterOption[] {
  const visible = (f: EmployeeAuditField) =>
    (access.canViewMessengers || !MESSENGER_FIELDS.has(f)) &&
    (access.canViewSalaryScalars || f !== 'nextReviewDate') &&
    (access.canViewEducation || f !== 'education');
  return HISTORY_FIELD_ORDER.filter(visible).map((value) => ({
    value,
    label: HISTORY_FIELD_LABELS[value],
  }));
}

/**
 * Newest save first, and within one save (rows sharing createdAt, whose server
 * order is by random id) in HISTORY_FIELD_ORDER, i.e. alphabetical by field
 * label, so the feed and the filter agree. The list is newest first, so the
 * `deleted` row (the newest event) goes before the field rows and the `created`
 * row (the oldest event) after them.
 */
export function orderHistoryEntries(entries: EmployeeAuditEntry[]): EmployeeAuditEntry[] {
  const rank = (e: EmployeeAuditEntry) => {
    if (e.action === 'deleted') return -1;
    if (e.action === 'created') return HISTORY_FIELD_ORDER.length;
    return e.field === null ? -1 : HISTORY_FIELD_ORDER.indexOf(e.field);
  };
  const time = (e: EmployeeAuditEntry) => new Date(e.createdAt).getTime();
  return entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => time(b.e) - time(a.e) || rank(a.e) - rank(b.e) || a.i - b.i)
    .map((x) => x.e);
}

/** The look of an entry on the Job tab's history timeline [HW-795]. */
export type HistoryTimelineTone = 'event' | 'change' | 'danger';
export type HistoryTimelineIcon =
  | 'created'
  | 'deleted'
  | 'lifecycle'
  | 'role'
  | 'org'
  | 'people'
  | 'date'
  | 'detail';

// Probation- and termination-like changes: the old timeline's amber events.
const EVENT_FIELDS = new Set<EmployeeAuditField>(['status', 'probationEndDate', 'terminationDate']);

const ICON_BY_FIELD: Partial<Record<EmployeeAuditField, HistoryTimelineIcon>> = {
  status: 'lifecycle',
  probationEndDate: 'lifecycle',
  terminationDate: 'lifecycle',
  position: 'role',
  seniorityLevel: 'role',
  employmentType: 'role',
  department: 'org',
  division: 'org',
  managers: 'people',
  reportsTo: 'people',
  startDate: 'date',
  nextReviewDate: 'date',
  dateOfBirth: 'date',
};

/**
 * The dot colour and icon of an entry on the Job tab's timeline, in the old
 * timeline's palette: red for a deactivation (status → terminated) and a
 * deletion, amber for the other status, probation and termination changes,
 * the default green for everything else.
 */
export function historyTimelineStyle(entry: EmployeeAuditEntry): {
  tone: HistoryTimelineTone;
  icon: HistoryTimelineIcon;
} {
  if (entry.action === 'created') return { tone: 'change', icon: 'created' };
  if (entry.action === 'deleted') return { tone: 'danger', icon: 'deleted' };
  const field = entry.field;
  if (field === null) return { tone: 'change', icon: 'detail' };
  if (field === 'status' && entry.newValue === 'terminated') {
    return { tone: 'danger', icon: 'lifecycle' };
  }
  return {
    tone: EVENT_FIELDS.has(field) ? 'event' : 'change',
    icon: ICON_BY_FIELD[field] ?? 'detail',
  };
}

const DATE_FIELDS = new Set<EmployeeAuditField>([
  'startDate',
  'probationEndDate',
  'terminationDate',
  'nextReviewDate',
  'dateOfBirth',
]);

// The words the profile card's status badge uses.
const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  probation: 'Probation',
  onboarding: 'Onboarding',
  offboarding: 'Offboarding',
  terminated: 'Deactivated',
};

const SOURCE_LABELS: Record<EmployeeAuditSource, string> = {
  ui: 'Unknown user',
  peopleforce: 'PeopleForce sync',
  preboarding: 'Pre-boarding form',
  recruitment: 'Recruitment',
  system: 'System',
};

export function formatAuditValue(field: EmployeeAuditField, value: AuditValue): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length > 0 ? value.map((r) => r.label).join(', ') : '—';
  if (typeof value === 'object') return value.label;
  if (DATE_FIELDS.has(field)) return formatDate(value, 'short') || value;
  if (field === 'status') return STATUS_LABELS[value] ?? value;
  return value;
}

export function describeEntry(entry: EmployeeAuditEntry): {
  title: string;
  from?: string;
  to?: string;
} {
  if (entry.action === 'created') return { title: 'Employee created' };
  if (entry.action === 'deleted') return { title: 'Employee deleted' };
  const field = entry.field as EmployeeAuditField;
  // Recorded as a fact only — the row holds no value from the block, and none
  // is shown even if one were there: History readers need not hold
  // employees.payment-details.view. [HW-1008]
  if (field === 'paymentDetails') {
    const verb =
      entry.oldValue === null ? 'added' : entry.newValue === null ? 'removed' : 'changed';
    return { title: `Payment details ${verb}` };
  }
  return {
    title: HISTORY_FIELD_LABELS[field] ?? field,
    from: formatAuditValue(field, entry.oldValue as AuditValue),
    to: formatAuditValue(field, entry.newValue as AuditValue),
  };
}

/** Initials for the avatar fallback: the actor's name, else the source label. */
export function actorInitials(entry: EmployeeAuditEntry): string {
  const name = entry.actor?.name ?? SOURCE_LABELS[entry.source] ?? 'System';
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('') || 'S'
  );
}

export function describeActor(entry: EmployeeAuditEntry): string {
  if (!entry.actor) return SOURCE_LABELS[entry.source] ?? 'System';
  return entry.impersonator
    ? `${entry.impersonator.name} (signed in as ${entry.actor.name})`
    : entry.actor.name;
}

export type HistoryFooterState =
  | 'loading-more'
  | 'refreshing'
  | 'next-page-error'
  | 'refresh-error'
  | 'load-more'
  | 'end';

/**
 * What the footer under a loaded history shows. A failed next page and a failed
 * refresh of the pages already shown are told apart, because each is retried by
 * a different call (fetchNextPage vs refetch).
 */
export function historyFooterState(q: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  isRefetchError: boolean;
  isRefetching: boolean;
}): HistoryFooterState {
  if (q.isFetchingNextPage) return 'loading-more';
  if (q.isFetchNextPageError) return 'next-page-error';
  // While a refetch runs, isRefetchError still carries the previous failure
  // (status stays 'error'), so it is not a failed refresh yet: show "Refreshing..."
  // (which also keeps the scroll sentinel from fetching a page over the refetch).
  if (q.isRefetching && q.isRefetchError) return 'refreshing';
  if (q.isRefetchError) return 'refresh-error';
  return q.hasNextPage ? 'load-more' : 'end';
}

/**
 * Whether the scroll sentinel may fetch the next page by itself. Never after a
 * failure: the observer fires again on every re-render cycle, so auto-fetching
 * then would retry forever; only the Retry button retries.
 */
export function historyAutoLoads(state: HistoryFooterState): boolean {
  return state === 'load-more';
}

/** How many entries the Job tab's "Position & History" card shows [HW-795]. */
export const HISTORY_PREVIEW_SIZE = 3;

/**
 * The newest `size` entries of a small newest-first page (the preview fetches
 * 20), in the tab's order, and whether there is more history. It orders and
 * slices a page larger than `size` rather than asking the server for `size`
 * rows: rows of one save share createdAt and the server breaks that tie by
 * random id, so a short server page could pick other fields of that save than
 * the tab shows first.
 */
export function historyPreview(
  firstPage: EmployeeAuditEntry[],
  hasNextPage: boolean,
  size: number = HISTORY_PREVIEW_SIZE
): { entries: EmployeeAuditEntry[]; hasMore: boolean } {
  return {
    entries: orderHistoryEntries(firstPage).slice(0, size),
    hasMore: firstPage.length > size || hasNextPage,
  };
}
